/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import { FunctionComponent, useState, useEffect, useRef, ReactNode } from 'react';
import PopOver from '../../Utility/PopOver/PopOver';
import OutsideClickHandler from '../../../vendor/OutsideClickHandler';
import { Color } from '../../DataDisplay/Icon/Icon';
import Button from '../../Input/Button/Button';
import styles from './Tooltip.module.scss';

import { withStyles } from '../../../utils';

const classes = withStyles(styles);

export type TooltipProps = {
  information?: ReactNode;
  type?: 'hover' | 'dialogue';
  icon?: 'info' | 'question';
  buttonWidth?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft';
  positionY?: string;
  positionX?: string;
  hasArrow?: boolean;
  hasButton?: boolean;
  color?: string;
  bgColor?: string;
  borderColor?: string;
  width?: string;
  iconColor?: Color;
  iconWidth?: string;
  isDisabled?: boolean;
  isDialogueVisible?: boolean;
  customElement?: React.JSX.Element;
  fontSize?: string;
  variant?: 'outlined';
  textAlign?: 'left' | 'right' | 'center';
  onClick?: (e: boolean) => void;
  popoverClassName?: string;
};

type FC = FunctionComponent<TooltipProps>;

const Tooltip: FC = ({
  type = 'hover',
  information,
  icon,

  color,
  position = 'bottom',
  positionY,
  positionX,
  hasArrow = true,
  hasButton = true,
  isDisabled = false,
  isDialogueVisible,
  customElement,
  iconColor,

  width,
  bgColor,
  borderColor,
  fontSize,
  variant,
  buttonWidth = variant === 'outlined' ? '25px' : '18px',
  iconWidth = variant === 'outlined' ? '10px' : '12px',
  textAlign,
  onClick = () => {},
  popoverClassName,
}: TooltipProps) => {
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoverDuration, setHoverDuration] = useState(0);
  const [hasEnter, setHasEnter] = useState(false);
  const [positionInfo, setPositionInfo] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [dynamicPosition, setDynamicPosition] = useState(position);
  const iconRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    window.innerWidth <= 768 ? setIsMobile(true) : setIsMobile(false);
  }, []);

  useEffect(() => {
    if (type === 'dialogue' && isDialogueVisible) {
      setIsInfoVisible(true);
    } else {
      setIsInfoVisible(false);
    }
  }, [isDialogueVisible]);

  const mouseOver = () => {
    setHasEnter(false);
    if (type === 'hover' && !isMobile && !isDisabled) {
      setHoverDuration(300);
      setIsInfoVisible(true);
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsInfoVisible(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const mouseLeave = () => {
    if (type === 'hover' && !isMobile && !isDisabled) {
      setHasEnter(false);
      setTimeout(() => {
        setIsInfoVisible(false);
        setHoverDuration(0);
      }, hoverDuration);
    }
  };

  const infoContainerMouseOver = () => {
    if (type === 'hover' && !isMobile) {
      setHoverDuration(300);
      setIsInfoVisible(true);
      setHasEnter(true);
    }
  };

  const infoContainerMouseLeave = () => {
    if (type === 'hover' && !isMobile) {
      setHasEnter(false);
      setTimeout(() => {
        setIsInfoVisible(false);
        setHoverDuration(0);
      }, hoverDuration);
    }
  };
  useEffect(() => {
    if (isInfoVisible) {
      // Small delay to ensure the tooltip is rendered before calculating position
      const timer = setTimeout(() => {
        calculatePosition();
      }, 10);

      return () => clearTimeout(timer);
    }

    const close = () => {
      setIsInfoVisible(false);
    };

    window.addEventListener('resize', close);

    return () => window.removeEventListener('resize', close);
  }, [isInfoVisible, information, width]);

  function calculatePosition() {
    if (isInfoVisible && iconRef.current && containerRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      const tooltipRect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate available space in each direction
      const spaceTop = iconRect.top;
      const spaceBottom = viewportHeight - iconRect.bottom;
      const spaceLeft = iconRect.left;
      const spaceRight = viewportWidth - iconRect.right;

      // Determine the best position based on available space and original preference
      let bestPosition = position;

      // Check if preferred position has enough space
      const tooltipWidth = tooltipRect.width || 300; // fallback width
      const tooltipHeight = tooltipRect.height || 100; // fallback height

      switch (position) {
        case 'right':
          if (spaceRight < tooltipWidth) {
            // Try left, then top, then bottom
            if (spaceLeft >= tooltipWidth) {
              bestPosition = 'left';
            } else if (spaceTop >= tooltipHeight) {
              bestPosition = 'top';
            } else if (spaceBottom >= tooltipHeight) {
              bestPosition = 'bottom';
            } else {
              bestPosition = 'left'; // fallback to left even if not enough space
            }
          }
          break;
        case 'left':
          if (spaceLeft < tooltipWidth) {
            // Try right, then top, then bottom
            if (spaceRight >= tooltipWidth) {
              bestPosition = 'right';
            } else if (spaceTop >= tooltipHeight) {
              bestPosition = 'top';
            } else if (spaceBottom >= tooltipHeight) {
              bestPosition = 'bottom';
            } else {
              bestPosition = 'right'; // fallback to right even if not enough space
            }
          }
          break;
        case 'top':
        case 'topLeft':
        case 'topRight':
          if (spaceTop < tooltipHeight) {
            // Try bottom variants, then left, then right
            if (spaceBottom >= tooltipHeight) {
              bestPosition = position === 'topLeft' ? 'bottomLeft' : 'bottom';
            } else if (spaceLeft >= tooltipWidth) {
              bestPosition = 'left';
            } else if (spaceRight >= tooltipWidth) {
              bestPosition = 'right';
            } else {
              bestPosition = 'bottom'; // fallback
            }
          }
          break;
        case 'bottom':
        case 'bottomLeft':
          if (spaceBottom < tooltipHeight) {
            // Try top variants, then left, then right
            if (spaceTop >= tooltipHeight) {
              bestPosition = position === 'bottomLeft' ? 'topLeft' : 'top';
            } else if (spaceLeft >= tooltipWidth) {
              bestPosition = 'left';
            } else if (spaceRight >= tooltipWidth) {
              bestPosition = 'right';
            } else {
              bestPosition = 'top'; // fallback
            }
          }
          break;
        default:
          // For any other position, default to collision detection for 'bottom'
          if (spaceBottom < tooltipHeight) {
            if (spaceTop >= tooltipHeight) {
              bestPosition = 'top';
            } else if (spaceLeft >= tooltipWidth) {
              bestPosition = 'left';
            } else if (spaceRight >= tooltipWidth) {
              bestPosition = 'right';
            }
          }
      }

      setDynamicPosition(bestPosition);
      setPositionInfo({
        top: iconRect.top,
        left: iconRect.left,
        width: iconRect.width,
        height: iconRect.height,
      });
    }
  }
  const [transformStyles, setTransformStyles] = useState<React.CSSProperties>({});
  useEffect(() => {
    if (containerRef.current && isInfoVisible) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Only apply additional transform adjustments for fine-tuning
      let translateX = 0;

      // For right and left positioned tooltips, ensure they don't go off-screen
      if (dynamicPosition === 'right' && rect.right > viewportWidth) {
        translateX = viewportWidth - rect.right - 10;
      } else if (dynamicPosition === 'left' && rect.left < 0) {
        translateX = Math.abs(rect.left) + 10;
      }

      setTransformStyles({
        transform: translateX !== 0 ? `translateX(${translateX}px)` : undefined,
      });
    }
  }, [
    width,
    fontSize,
    information,
    positionInfo,
    isInfoVisible,
    dynamicPosition,
    containerRef.current?.clientTop,
    containerRef.current?.clientLeft,
  ]);
  return (
    <OutsideClickHandler onOutsideClick={() => isDialogueVisible && onClick(false)} width="auto">
      <div className={[styles.container, styles[variant || '']].join(' ')}>
        <div
          className={[
            styles.icon,
            styles[getColorClass()],
            styles[getDisabledClass()],
            hasButton ? 'hasButton' : undefined,
          ].join(' ')}
          onMouseOver={mouseOver}
          onMouseLeave={mouseLeave}
          ref={iconRef}
        >
          {hasButton && (
            <Button
              label=""
              type="button"
              width={buttonWidth}
              height={buttonWidth}
              background={bgColor || (variant === 'outlined' ? '#FBFBFB' : type === 'hover' ? '#EFEFEF' : '#5F5F5F')}
              color={color || (type === 'hover' ? 'secondary' : 'primary')}
              icon={icon === 'info' ? 'information' : 'question'}
              iconColor={iconColor || (variant === 'outlined' ? 'medium-dark' : 'grey')}
              iconLocation="center"
              iconWidth={iconWidth}
              iconHeight={iconWidth}
              iconSize="xsm"
              textAlign="center"
              borderColor={borderColor}
              noPadding
              noShadow
              isCircle
              isDisabled={isDisabled}
              onClick={() => {
                type === 'dialogue'
                  ? onClick(!isDialogueVisible)
                  : type === 'hover' && isMobile
                    ? setIsInfoVisible(!isInfoVisible)
                    : () => {};
              }}
            />
          )}
          {customElement && customElement}
        </div>
        <PopOver style={positionInfo}>
          <div
            className={classes(
              'informationContainer',
              getPositionClass(),
              getOpacityClass(),
              hasArrow && 'hasArrow',
              popoverClassName,
            )}
            style={{ transform: `translate(${positionX || ''}, ${positionY || ''})` }}
            onMouseOver={infoContainerMouseOver}
            onMouseLeave={infoContainerMouseLeave}
          >
            {hasArrow && <span className={styles.arrow} />}

            <div
              className={styles.information}
              ref={containerRef}
              style={{ ...transformStyles, maxWidth: width, fontSize, textAlign }}
            >
              {information}
            </div>
          </div>
        </PopOver>
      </div>
    </OutsideClickHandler>
  );

  function getPositionClass() {
    switch (dynamicPosition) {
      case 'top':
        return 'top';
      case 'bottom':
        return 'bottom';
      case 'bottomLeft':
        return 'bottomLeft';
      case 'left':
        return 'left';
      case 'topLeft':
        return 'topLeft';
      case 'topRight':
        return 'topRight';
      default:
        return 'right';
    }
  }

  function getColorClass() {
    return (isInfoVisible || hasEnter) && hasButton ? 'color' : '';
  }

  function getOpacityClass() {
    return isInfoVisible || hasEnter ? 'visible' : '';
  }

  function getDisabledClass() {
    return isDisabled && hasButton ? 'disabled' : '';
  }
};

export default Tooltip;
